import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import { Play, Upload, DollarSign, Radio, BarChart3, Palette, ListVideo, Users, Globe2, Check, ChevronRight } from 'lucide-react';
import { LANDING_FEATURES, LANDING_FAQ, PLANS } from '../mock';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useApp } from '../context/AppContext';
import PwaInstallButton from '../components/PwaInstallButton';

const FEATURE_ICONS = [Upload, Play, DollarSign, Radio, BarChart3, Palette, ListVideo, Users, Globe2];

export default function Landing() {
  const nav = useNavigate();
  const { user } = useApp();

  const primaryHref = user ? '/home' : '/signup';

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-white">
      {/* Top nav */}
      <header className="sticky top-0 z-40 backdrop-blur bg-[#0a0a0c]/80 border-b border-white/5">
        <div className="max-w-[1280px] mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <nav className="hidden md:flex items-center gap-6 text-[14px] text-white/75">
            <a href="#features" className="hover:text-white">Features</a>
            <a href="#plans" className="hover:text-white">Pricing</a>
            <a href="#faq" className="hover:text-white">FAQ</a>
            <Link to="/memberships" className="hover:text-white">Memberships</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" className="btn-ghost hidden sm:inline-flex">Sign in</Link>
            <Link to={primaryHref} className="btn-primary">
              {user ? 'Open app' : 'Get started'} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(1000px 500px at 10% -10%, rgba(255,46,99,0.28), transparent 55%), radial-gradient(800px 400px at 100% 10%, rgba(34,211,238,0.15), transparent 60%)' }} />
        <div className="relative max-w-[1280px] mx-auto px-5 pt-16 md:pt-24 pb-14 grid lg:grid-cols-[1.05fr_1fr] gap-12 items-center">
          <div>
            <div className="chip"><span className="live-dot" /> Now live — open beta for indie musicians</div>
            <h1 className="mt-5 font-display text-[44px] md:text-[68px] lg:text-[80px] leading-[0.95]">
              The music career <span className="brand-gradient">OS</span> for independent artists.
            </h1>
            <p className="mt-5 text-white/75 text-[16px] md:text-[18px] max-w-[580px] leading-relaxed">
              ReaperCast is where indie musicians release music, learn the business, and get AI coaching from specialists who actually know the game. Radio rotation, Academy, Specialist Agents, Research Vault, Knowledge Hub — plus <span className="text-white font-semibold">VidCreForge video hosting included free</span>.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link to="/reapercast/signup" className="btn-primary" data-testid="landing-musician-cta">
                Claim your ReaperCast <ChevronRight className="w-4 h-4" />
              </Link>
              <Link to="/tour" className="btn-ghost" data-testid="landing-tour-cta">Take a 60-sec tour</Link>
              <Link to="/signup/listener" className="btn-ghost" data-testid="landing-listener-cta">I'm here to listen</Link>
              <Link to="/signup" className="btn-ghost text-[12.5px] opacity-70" data-testid="landing-video-cta">Video only (VidCreForge)</Link>
              <PwaInstallButton variant="ghost" label="Install the app" testid="landing-pwa-install" />
            </div>
            <div className="mt-6 flex items-center gap-6 text-[13px] text-white/60">
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#22d3ee]" /> Free forever tier</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#22d3ee]" /> VidCreForge video included</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#22d3ee]" /> No credit card</span>
            </div>
          </div>

          {/* Hero preview card */}
          <div className="relative">
            <div className="card-shell p-3 hover-lift">
              <div className="thumb">
                <img src="https://images.unsplash.com/photo-1601944179066-29786cb9d32a?w=1400" alt="preview" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#ff2e63] grid place-items-center shadow-[0_10px_30px_rgba(255,46,99,0.5)]">
                    <Play className="w-7 h-7 md:w-8 md:h-8 text-white fill-white ml-1" />
                  </div>
                </div>
                <div className="absolute left-4 top-4 chip"><span className="live-dot" /> Live preview</div>
              </div>
              <div className="p-3">
                <div className="text-[14px] font-semibold">Neon Skyline — A Short Cinematic</div>
                <div className="text-[12.5px] text-white/55 mt-1">CinemaVault • 1.2M views • 2 days ago</div>
              </div>
            </div>
            <div className="hidden md:block absolute -right-6 -bottom-6 card-shell p-4 w-56">
              <div className="text-[11px] text-white/55 uppercase tracking-wider">Monthly revenue</div>
              <div className="font-display text-[32px] leading-none mt-1">$12,480</div>
              <div className="text-[12px] text-[#22d3ee] mt-1">+18.2% vs last month</div>
              <div className="mt-3 h-10 flex items-end gap-1">
                {[24,38,30,55,48,62,70,85].map((h,i) => (
                  <div key={i} className="flex-1 rounded-sm" style={{ height: `${h}%`, background: 'linear-gradient(180deg,#ff2e63,#4a0a19)' }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust row */}
      <section className="py-8 border-y border-white/5 bg-white/[0.015]">
        <div className="max-w-[1280px] mx-auto px-5 flex flex-wrap items-center justify-center gap-6 md:gap-12 text-white/50 text-[13px]">
          <span className="font-display text-[20px] tracking-[0.2em]">CINEMAVAULT</span>
          <span className="font-display text-[20px] tracking-[0.2em]">TECHORBIT</span>
          <span className="font-display text-[20px] tracking-[0.2em]">GROOVE LAB</span>
          <span className="font-display text-[20px] tracking-[0.2em]">GRIDLINE</span>
          <span className="font-display text-[20px] tracking-[0.2em]">BRIGHTLEARN</span>
          <span className="font-display text-[20px] tracking-[0.2em]">MINDFUL</span>
        </div>
      </section>

      {/* What's inside ReaperCast — the 5 pillars */}
      <section id="features" className="py-20">
        <div className="max-w-[1280px] mx-auto px-5">
          <div className="text-center max-w-[780px] mx-auto">
            <div className="chip">What's inside</div>
            <h2 className="font-display text-[36px] md:text-[52px] leading-[1.05] mt-4">Five products. <span className="brand-gradient">One account.</span></h2>
            <p className="text-white/70 mt-4 text-[15.5px]">Every ReaperCast account includes all five — free forever tier, upgrade for more.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
            {[
              { t: 'ReaperCast Radio',       d: '24/7 audio rotation for your releases. No gatekeepers, no playlist curators — upload, it airs.', color: '#ff2e63' },
              { t: 'Music Biz Academy',     d: '7 tracks, 38 lessons, 7 specialist AI agents unlocked by learning. Copyright, royalties, publishing, licensing.', color: '#ffcf52' },
              { t: 'Specialist AI Agents',  d: 'Claude-powered coaches for publishing, royalties, rights, licensing, release strategy. Your personal advisory board.', color: '#a78bfa' },
              { t: 'Knowledge Hub',         d: 'Ebooks, audiobooks, workbooks, and podcasts. Educators publish here, artists and learners consume.', color: '#22d3ee' },
              { t: 'Research Vault',        d: 'Save any AI response as a content/product draft. Turn coaching into ebooks, pitches, release plans.', color: '#fb7185' },
              { t: 'VidCreForge Video',     d: 'Full video hosting and monetization — music videos, vlogs, behind-the-scenes. Included free in every plan.', color: '#ff7a00' },
            ].map((f, i) => (
              <div key={i} className="card-shell p-6 hover-lift">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: f.color + '22', border: `1px solid ${f.color}44` }}>
                  <span style={{ color: f.color, fontWeight: 800 }}>{String(i + 1).padStart(2, '0')}</span>
                </div>
                <h3 className="font-display text-[22px] mt-4 tracking-wide">{f.t}</h3>
                <p className="text-white/65 text-[14px] mt-1 leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Storefront preview strip */}
      <section className="pb-20">
        <div className="max-w-[1280px] mx-auto px-5">
          <div className="card-shell overflow-hidden">
            <div className="p-6 md:p-10 grid lg:grid-cols-[1fr_1.2fr] gap-8 items-center">
              <div>
                <div className="chip">VidCreForge · included add-on</div>
                <h3 className="font-display text-[32px] md:text-[42px] mt-3 leading-[1.05]">
                  Video is <span className="brand-gradient">included</span>, not the main event.
                </h3>
                <p className="text-white/65 mt-3 text-[14.5px] leading-relaxed">
                  Every ReaperCast account ships with VidCreForge — a full video hosting &amp; monetization platform for your music videos, BTS, tour vlogs, and lyric videos. Storefront, ads, tips, memberships — same account.
                </p>
                <div className="mt-5 flex gap-2">
                  <button onClick={() => nav(primaryHref)} className="btn-primary">Try the demo</button>
                  <a href="#plans" className="btn-ghost">See plans</a>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  'https://images.unsplash.com/photo-1637806631554-bcfe2c618058?w=500',
                  'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=500',
                  'https://images.pexels.com/photos/32141311/pexels-photo-32141311.jpeg?w=500',
                  'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=500',
                  'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=500',
                  'https://images.unsplash.com/photo-1556438064-2d7646166914?w=500',
                ].map((src, i) => (
                  <div key={i} className="thumb"><img src={src} alt="tile" /></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Plans */}
      <section id="plans" className="py-20 bg-white/[0.015] border-y border-white/5">
        <div className="max-w-[1280px] mx-auto px-5">
          <div className="text-center max-w-[780px] mx-auto">
            <div className="chip">Plans</div>
            <h2 className="font-display text-[36px] md:text-[52px] leading-[1.05] mt-4">Start free. <span className="brand-gradient">Scale on your terms.</span></h2>
            <p className="text-white/65 mt-3">Transparent pricing — no surprises, cancel anytime.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
            {PLANS.map(p => (
              <div key={p.id} className={`card-shell p-6 flex flex-col ${p.highlight ? 'ring-2 ring-[#ff2e63]/50 relative' : ''}`}>
                {p.highlight && <div className="absolute -top-3 left-6 chip active">Most popular</div>}
                <div className="font-display text-[26px]">{p.name}</div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="font-display text-[40px]">${p.price}</span>
                  <span className="text-white/55 text-[13px]">{p.period}</span>
                </div>
                <ul className="mt-4 space-y-2 text-[13.5px] text-white/80 flex-1">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2"><Check className="w-4 h-4 text-[#22d3ee] mt-0.5 shrink-0" /><span>{f}</span></li>
                  ))}
                </ul>
                <button onClick={() => nav(primaryHref)} className={`mt-5 ${p.highlight ? 'btn-primary' : 'btn-ghost'} w-full`}>
                  {p.price === 0 ? 'Start free' : 'Choose ' + p.name}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20">
        <div className="max-w-[880px] mx-auto px-5">
          <div className="text-center">
            <div className="chip">FAQ</div>
            <h2 className="font-display text-[36px] md:text-[52px] mt-4">Good questions. <span className="brand-gradient">Straight answers.</span></h2>
          </div>
          <Accordion type="single" collapsible className="mt-10 space-y-3">
            {LANDING_FAQ.map((f, i) => (
              <AccordionItem key={i} value={`q${i}`} className="card-shell px-5 border-0">
                <AccordionTrigger className="text-left text-[15px] md:text-[16px] font-semibold py-4 hover:no-underline hover:text-[#ff6b8a]">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-white/70 text-[14px] pb-4 leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16">
        <div className="max-w-[1100px] mx-auto px-5">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 p-10 md:p-14 text-center" style={{ background: 'radial-gradient(800px 300px at 50% 0%, rgba(255,46,99,0.28), transparent 70%), #0e0e12' }}>
            <h3 className="font-display text-[36px] md:text-[54px] leading-[1.05]">Ready to stream on <span className="brand-gradient">your own terms?</span></h3>
            <p className="text-white/70 mt-3 max-w-[640px] mx-auto">Launch your channel in minutes. Upload, go live and monetize — all from one dashboard.</p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <button onClick={() => nav(primaryHref)} className="btn-primary">Create free account <ChevronRight className="w-4 h-4" /></button>
              <Link to="/login" className="btn-ghost">Sign in</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-10">
        <div className="max-w-[1280px] mx-auto px-5 flex flex-col md:flex-row items-center justify-between gap-4">
          <Logo />
          <div className="flex flex-wrap items-center gap-4 text-[13px] text-white/55">
            <a href="#" className="hover:text-white">Privacy</a>
            <a href="#" className="hover:text-white">Terms</a>
            <a href="#" className="hover:text-white">Security</a>
            <a href="#" className="hover:text-white">Contact</a>
          </div>
          <div className="text-[12px] text-white/40">© {new Date().getFullYear()} VidCreForge Inc.</div>
        </div>
      </footer>
    </div>
  );
}
